import React, { useEffect, useMemo, useState } from 'react'
import '../Settings/settings.css'
import { Button, Input, Modal, Divider, Typography, Tooltip, Space, notification, message } from 'antd'
import { CopyOutlined } from '@ant-design/icons'
import { GiConfirmed } from 'react-icons/gi'
import { useAuth } from '../../context/AuthProvider'
import { useCustomer } from '../../context/CustomerProvider'
import ActiveBrandBlock from './ActiveBrandBlock'

function openPopup(message, description, icon, bg) {
  notification.open({
    message,
    description,
    icon,
    placement: 'topRight',
    style: { 'background-color': bg },
    duration: 4,
  })
}

export default function BrandSelectionCard({
  brandName = '',
  onBrandNameChange,
  onSaveBrandName,
  brandNameLockStyle = {},
  canEditBrandName = true,
}) {
  const { currentUser, brandId, brands, setSelectedBrand, triggerBrand } = useAuth()
  const { createBrand, addBrand } = useCustomer()

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false)
  const [newBrandName, setNewBrandName] = useState('')
  const [userIdToAdd, setUserIdToAdd] = useState('')
  const [isBrandNameEditing, setIsBrandNameEditing] = useState(false)
  const [isSavingBrandName, setIsSavingBrandName] = useState(false)
  const [isCreatingBrand, setIsCreatingBrand] = useState(false)
  const [isAddingUser, setIsAddingUser] = useState(false)

  useEffect(() => {
    setIsBrandNameEditing(false)
  }, [brandId])

  const currentBrand = useMemo(() => {
    if (!brandId || !brands?.length) return { id: null, name: null }
    return brands.find((b) => b.id === brandId) ?? { id: brandId, name: null }
  }, [brandId, brands])

  const hasBrand = Boolean(brandId)
  const userUid = currentUser?.uid ?? ''

  const copyUserId = async () => {
    if (!userUid) return
    try {
      await navigator.clipboard.writeText(userUid)
      message.success('User ID copied')
    } catch {
      message.error('Could not copy')
    }
  }

  const handleCreateOk = async () => {
    const nameSnapshot = newBrandName
    setIsCreatingBrand(true)
    try {
      const response = await createBrand(newBrandName)
      if (response?.success) {
        await triggerBrand()
        setNewBrandName('')
      }
      setIsCreateModalOpen(false)
      openPopup(
        'Brand Created!',
        `You have successfully created a brand with the name of ${nameSnapshot}.`,
        <GiConfirmed />,
        '#D1FFBD'
      )
    } catch (e) {
      setIsCreateModalOpen(false)
      console.error(e)
    } finally {
      setIsCreatingBrand(false)
    }
  }

  const handleCreateCancel = () => {
    setNewBrandName('')
    setIsCreateModalOpen(false)
  }

  const handleAddUserOk = async () => {
    const snapshotName = currentBrand.name || 'the brand'
    setIsAddingUser(true)
    try {
      const response = await addBrand(userIdToAdd)
      if (response) {
        setIsAddUserModalOpen(false)
        setUserIdToAdd('')
        openPopup(
          'Successfully added user to brand!',
          `You have successfully added the new user to ${snapshotName}.`,
          <GiConfirmed />,
          '#D1FFBD'
        )
      } else {
        setIsAddUserModalOpen(false)
        openPopup(
          'Failed to add that user to brand. Likely invalid user ID.',
          `The other user's ID is shown in their account (e.g. on their Brand tab under Users).`,
          <GiConfirmed />,
          '#FFCCCC'
        )
      }
    } catch (error) {
      setIsAddUserModalOpen(false)
      openPopup(
        'Failed to add user',
        `Could not add user to ${snapshotName}.`,
        <GiConfirmed />,
        '#FFCCCC'
      )
      console.error(error)
    } finally {
      setIsAddingUser(false)
    }
  }

  const handleAddUserCancel = () => {
    setUserIdToAdd('')
    setIsAddUserModalOpen(false)
  }

  const handleBrandNameActionClick = async () => {
    if (!isBrandNameEditing) {
      setIsBrandNameEditing(true)
      return
    }
    try {
      setIsSavingBrandName(true)
      await onSaveBrandName?.()
      setIsBrandNameEditing(false)
    } catch {
      // Keep editing so the user can retry after a failed save
    } finally {
      setIsSavingBrandName(false)
    }
  }

  return (
    <div className="brand-workspace">
      <ActiveBrandBlock />

      <Divider className="brand-workspace__divider" />

      <section className="brand-workspace__section" aria-labelledby="brand-new-heading">
        <h2 id="brand-new-heading" className="title-2">
          New brand
        </h2>
        <Typography.Text type="secondary" className="brand-workspace__hint">
          Create an empty brand workspace, then select it above.
        </Typography.Text>
        <Button type="default" onClick={() => setIsCreateModalOpen(true)}>
          Create a new brand
        </Button>
      </section>

      <Divider className="brand-workspace__divider" />

      <section className="brand-workspace__section" aria-labelledby="brand-users-heading">
        <h2 id="brand-users-heading" className="title-2">
          Users
        </h2>
        <Typography.Text type="secondary" className="brand-workspace__hint">
          Your user ID is{' '}
          <span className="brand-workspace__uid-inline">
            <code className="brand-workspace__code">{userUid || '—'}</code>
            <Tooltip title="Copy user ID">
              <Button
                type="text"
                size="small"
                className="brand-workspace__copy-uid"
                icon={<CopyOutlined />}
                onClick={copyUserId}
                disabled={!userUid}
                aria-label="Copy user ID"
              />
            </Tooltip>
          </span>
          <br />
          Invite an existing user into the <strong>currently selected</strong> brand (requires their user ID).
        </Typography.Text>
        <Space className="brand-workspace__actions" wrap>
          <Tooltip title={hasBrand ? undefined : 'Select a brand first'}>
            <span className="brand-workspace__btn-wrap">
              <Button
                type="default"
                disabled={!hasBrand}
                onClick={() => hasBrand && setIsAddUserModalOpen(true)}
              >
                {`Add user to '${currentBrand.name || 'this brand'}'`}
              </Button>
            </span>
          </Tooltip>
        </Space>
      </section>

      <Divider className="brand-workspace__divider" />

      <section className="brand-workspace__section" aria-labelledby="brand-email-name-heading">
        <h2 id="brand-email-name-heading" className="title-2">
          Brand name
        </h2>
        <Typography.Text type="secondary" className="brand-workspace__hint">
          This is how we&apos;ll refer to ourselves in emails.
        </Typography.Text>
        <div className="brand-workspace__field brand-workspace__brand-name-field" style={brandNameLockStyle}>
          <Input
            placeholder="Kylie Skin"
            value={brandName}
            onChange={(e) => onBrandNameChange?.(e.target.value)}
            className="brand-workspace__input"
            autoComplete="off"
            disabled={!isBrandNameEditing}
          />
          <Button
            type="default"
            onClick={handleBrandNameActionClick}
            style={{ marginTop: 10 }}
            loading={isSavingBrandName}
            disabled={!canEditBrandName}
          >
            {isBrandNameEditing ? 'Save' : 'Edit'}
          </Button>
        </div>
        {!canEditBrandName && (
          <Typography.Text type="secondary" className="brand-workspace__hint">
            Brand name can only be edited by an admin.
          </Typography.Text>
        )}
      </section>

      <Modal
        centered
        title="Create a new Brand"
        open={isCreateModalOpen}
        onOk={handleCreateOk}
        onCancel={handleCreateCancel}
        okText="Create"
        okButtonProps={{ type: 'default', loading: isCreatingBrand }}
        cancelButtonProps={{ type: 'default', disabled: isCreatingBrand }}
        closable={!isCreatingBrand}
        maskClosable={!isCreatingBrand}
      >
        <Input
          placeholder="Enter your brand's name"
          value={newBrandName}
          onChange={(e) => setNewBrandName(e.target.value)}
          disabled={isCreatingBrand}
        />
      </Modal>

      <Modal
        centered
        title="Add a user to this brand"
        open={isAddUserModalOpen}
        onOk={handleAddUserOk}
        onCancel={handleAddUserCancel}
        okText="Add user"
        okButtonProps={{ type: 'default', loading: isAddingUser }}
        cancelButtonProps={{ type: 'default', disabled: isAddingUser }}
        closable={!isAddingUser}
        maskClosable={!isAddingUser}
      >
        <Input
          placeholder="Enter the user ID you'd like to add."
          value={userIdToAdd}
          onChange={(e) => setUserIdToAdd(e.target.value)}
          disabled={isAddingUser}
        />
      </Modal>
    </div>
  )
}
